<template>
  <div class="management-page-wrapper">
    <!-- Breadcrumb -->
    <div class="breadcrumb-container">
      <router-link to="/binance" class="breadcrumb-link">Home</router-link>
      <span class="breadcrumb-separator">></span>
      <span class="breadcrumb-current">Account Management</span>
    </div>

    <!-- Title and Stats Header -->
    <div class="page-header-row">
      <div class="page-title-group">
        <h1 class="page-title">Account Management</h1>
        <p class="page-subtitle">Manage users and permissions in the system</p>
      </div>

      <!-- Stats Section: Only Total Users card remains -->
      <div class="stats-container">
        <div class="stat-card">
          <div class="stat-icon-wrapper">
            <i class="fa-solid fa-users"></i>
          </div>
          <div class="stat-info">
            <span class="stat-label">Total Users</span>
            <span class="stat-value">{{ filteredUsers.length }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Filters & Action Bar -->
    <div class="filters-bar-card">
      <div class="filters-left-group">
        <!-- Search Input -->
        <div class="search-input-wrapper">
          <i class="fa-solid fa-magnifying-glass search-icon"></i>
          <input 
            type="text" 
            v-model="filters.search" 
            placeholder="Search by username or email..."
            class="search-input"
          />
        </div>

        <!-- Role Select -->
        <div class="select-filter-wrapper">
          <span class="filter-label">Role</span>
          <div class="select-input-container">
            <select v-model="filters.role" class="filter-select">
              <option value="All">All Roles</option>
              <option value="SYSTEM">System</option>
              <option value="ADMIN">Admin</option>
              <option value="NORMAL_USER">Normal User</option>
            </select>
            <i class="fa-solid fa-chevron-down select-arrow"></i>
          </div>
        </div>

        <!-- Status Select -->
        <div class="select-filter-wrapper">
          <span class="filter-label">Status</span>
          <div class="select-input-container">
            <select v-model="filters.status" class="filter-select">
              <option value="All">All Statuses</option>
              <option value="ACTIVE">Active</option>
              <option value="DISABLED">Disabled</option>
              <option value="DELETED">Deleted</option>
            </select>
            <i class="fa-solid fa-chevron-down select-arrow"></i>
          </div>
        </div>

        <!-- Reset Button -->
        <button @click="resetFilters" class="btn-reset" title="Reset Filters">
          <i class="fa-solid fa-rotate-right"></i>
          <span>Reset</span>
        </button>
      </div>

      <div class="filters-right-group" style="display: flex; gap: 8px;">
        <!-- Clone from Prod button -->
        <button @click="openCloneModal" class="btn-create" style="background-color: #2b3139; border: 1px solid #475569; color: #cbd5e1;">
          <i class="fa-solid fa-clone" style="color: #22d3ee; margin-right: 6px;"></i>
          <span>Clone from Prod</span>
        </button>
        <!-- Merge Account button -->
        <button @click="openMergeModal" class="btn-create" style="background-color: #2b3139; border: 1px solid #475569; color: #cbd5e1;">
          <i class="fa-solid fa-code-merge" style="color: #10b981; margin-right: 6px;"></i>
          <span>Merge Account</span>
        </button>
        <!-- Create Account button -->
        <button @click="openCreateModal" class="btn-create">
          <i class="fa-solid fa-plus"></i>
          <span>Create Account</span>
        </button>
      </div>
    </div>

    <!-- Main Table Card -->
    <div class="table-card">
      <div class="table-responsive">
        <table class="users-table">
          <thead>
            <tr>
              <th class="col-num">#</th>
              <th class="col-user">User</th>
              <th class="col-email">Email</th>
              <th class="col-role">Role</th>
              <th class="col-status">Status</th>
              <th class="col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="paginatedUsers.length === 0">
              <td colspan="6" class="no-records">No users found matching the filter criteria.</td>
            </tr>
            <tr v-for="(user, index) in paginatedUsers" :key="user.id">
              <td class="col-num text-muted">
                {{ (currentPage - 1) * itemsPerPage + index + 1 }}
              </td>
              <td class="col-user">
                <div class="user-cell">
                  <div class="avatar-circle" :style="{ backgroundColor: user.avatarColor || '#a78bfa' }">
                    <i class="fa-solid fa-user"></i>
                  </div>
                  <div class="user-meta">
                    <div class="user-username-row">
                      <span class="user-username">{{ user.username }}</span>
                      <span v-if="user.role === 'SYSTEM'" class="super-admin-badge">System</span>
                      <span v-if="user.role === 'ADMIN'" class="admin-badge">Admin</span>
                    </div>
                    <span class="user-fullname">{{ user.fullName }}</span>
                  </div>
                </div>
              </td>
              <td class="col-email">{{ user.email }}</td>
              <td class="col-role">
                <span :class="['role-badge', user.roleClass]">
                  {{ user.roleLabel }}
                </span>
              </td>
              <td class="col-status">
                <div :class="['status-bullet-container', user.statusClass]">
                  <span class="bullet"></span>
                  <span class="status-label-text">{{ user.statusLabel }}</span>
                </div>
              </td>
              <td class="col-actions">
                <div class="actions-buttons-group">
                  <button @click="openEditModal(user)" class="btn-action-icon edit" title="Edit User">
                    <i class="fa-solid fa-pen"></i>
                  </button>
                  <button @click="toggleStatus(user)" class="btn-action-icon lock" :disabled="isSelf(user)" :title="user.status === 'DISABLED' ? 'Enable User' : 'Disable User'">
                    <i :class="['fa-solid', user.status === 'DISABLED' ? 'fa-lock-open' : 'fa-lock']"></i>
                  </button>
                  <button @click="resetUserPassword(user)" class="btn-action-icon password" title="Reset Password">
                    <i class="fa-solid fa-key"></i>
                  </button>
                  <button @click="deleteUser(user)" class="btn-action-icon delete" :disabled="isSelf(user)" title="Delete User">
                    <i class="fa-solid fa-trash-can"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination Footer -->
      <div class="table-footer">
        <span class="footer-count-text">
          Showing {{ showingStart }} to {{ showingEnd }} of {{ filteredUsers.length }} users
        </span>

        <div class="footer-pagination-group">
          <!-- Limit Selector -->
          <div class="limit-selector-wrapper">
            <select v-model="itemsPerPage" @change="currentPage = 1" class="limit-select">
              <option :value="5">5 per page</option>
              <option :value="10">10 per page</option>
              <option :value="20">20 per page</option>
            </select>
            <i class="fa-solid fa-chevron-down select-arrow"></i>
          </div>

          <!-- Page navigation buttons -->
          <div class="pagination-buttons">
            <button 
              @click="currentPage = Math.max(1, currentPage - 1)" 
              :disabled="currentPage === 1"
              class="btn-page"
            >
              <i class="fa-solid fa-chevron-left"></i>
            </button>
            
            <button 
              v-for="page in totalPages" 
              :key="page"
              @click="currentPage = page"
              :class="['btn-page', { active: currentPage === page }]"
            >
              {{ page }}
            </button>

            <button 
              @click="currentPage = Math.min(totalPages, currentPage + 1)" 
              :disabled="currentPage === totalPages || totalPages === 0"
              class="btn-page"
            >
              <i class="fa-solid fa-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Form (Create & Edit) -->
    <transition name="modal-fade">
      <div v-if="modal.isOpen" class="modal-overlay">
        <div class="modal-card">
          <div class="modal-header">
            <h2 class="modal-title">{{ modal.mode === 'create' ? 'Create Account' : 'Edit User' }}</h2>
            <button @click="closeModal" class="btn-close-modal">
              <i class="fa-solid fa-xmark"></i>
            </button>
          </div>

          <form @submit.prevent="submitModal" class="modal-form">
            <div class="modal-form-grid">
              <!-- Full Name -->
              <div class="form-group">
                <label for="modalFullName">Full Name</label>
                <input 
                  id="modalFullName" 
                  type="text" 
                  v-model="modal.form.fullName" 
                  placeholder="John Doe"
                  required
                />
              </div>

              <div v-if="modal.mode === 'create'" class="form-group">
                <label for="modalPassword">Password</label>
                <div class="password-input-wrapper">
                  <input
                    id="modalPassword"
                    :type="showPassword ? 'text' : 'password'"
                    v-model="modal.form.password"
                    placeholder="At least 8 characters"
                    minlength="8"
                    required
                  />
                  <button
                    type="button"
                    class="password-toggle"
                    :aria-label="showPassword ? 'Hide password' : 'Show password'"
                    @click="showPassword = !showPassword"
                  >
                    <i :class="['fa-solid', showPassword ? 'fa-eye-slash' : 'fa-eye']"></i>
                  </button>
                </div>
              </div>

              <!-- Email -->
              <div class="form-group">
                <label for="modalEmail">Email Address</label>
                <input 
                  id="modalEmail" 
                  type="email" 
                  v-model="modal.form.email" 
                  placeholder="john@example.com"
                  required
                />
              </div>

              <div class="role-status-row">
                <!-- Role -->
                <div class="form-group">
                  <label for="modalRole">Role</label>
                  <div class="select-wrapper">
                    <select id="modalRole" v-model="modal.form.role">
                      <option v-if="isSystemCurrentUser" value="SYSTEM">System</option>
                      <option value="ADMIN">Admin</option>
                      <option value="NORMAL_USER">Normal User</option>
                    </select>
                    <i class="fa-solid fa-chevron-down select-arrow"></i>
                  </div>
                </div>

                <!-- Status -->
                <div class="form-group">
                  <label for="modalStatus">Status</label>
                  <div class="select-wrapper">
                    <select id="modalStatus" v-model="modal.form.status">
                      <option value="ACTIVE">Active</option>
                      <option value="DISABLED">Disabled</option>
                      <option value="DELETED">Deleted</option>
                    </select>
                    <i class="fa-solid fa-chevron-down select-arrow"></i>
                  </div>
                </div>
              </div>
            </div>

            <div class="modal-actions">
              <button type="button" @click="closeModal" class="btn-cancel">
                Cancel
              </button>
              <button type="submit" class="btn-save">
                {{ modal.mode === 'create' ? 'Create' : 'Save Changes' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </transition>

    <!-- Clone User Modal -->
    <transition name="modal-fade">
      <div v-if="cloneModal.isOpen" class="modal-overlay" @click.self="closeCloneModal">
        <div class="modal-card" style="max-width: 550px;">
          <div class="modal-header">
            <h2 class="modal-title">{{ cloneModal.isMerge ? 'Merge User Settings' : 'Clone User Settings' }}</h2>
            <button @click="closeCloneModal" class="btn-close-modal">
              <i class="fa-solid fa-xmark"></i>
            </button>
          </div>

          <form @submit.prevent="submitCloneModal" class="modal-form">
            
            <!-- Source User Selection -->
            <div class="form-group" style="margin-bottom: 15px; position: relative;">
              <label for="cloneSourceUser">Source User (Tài khoản nguồn)</label>
              <div class="custom-dropdown-container">
                <button 
                  type="button" 
                  class="custom-dropdown-trigger" 
                  @click="sourceDropdownOpen = !sourceDropdownOpen"
                >
                  <span v-if="selectedSourceUser">
                    {{ selectedSourceUser.fullName || selectedSourceUser.username || selectedSourceUser.email }} ({{ selectedSourceUser.email }})
                  </span>
                  <span v-else class="placeholder-text">Select Source User...</span>
                  <i class="fa-solid fa-chevron-down select-arrow" :class="{ rotated: sourceDropdownOpen }"></i>
                </button>
                
                <div v-show="sourceDropdownOpen" class="custom-dropdown-menu">
                  <div class="dropdown-options-list">
                    <div 
                      v-for="u in filteredSourceUsers" 
                      :key="u.id" 
                      class="dropdown-option-item"
                      :class="{ selected: cloneModal.userId === u.id }"
                      @click="selectSourceUser(u)"
                    >
                      {{ u.fullName || u.username || u.email }} ({{ u.email }})
                    </div>
                    <div v-if="filteredSourceUsers.length === 0" class="no-options-found">
                      No users found.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Destination User Options -->
            <div class="form-group" style="margin-top: 15px;">
              <label>Destination Account (Tài khoản đích)</label>
              <div class="destination-tabs">
                <button 
                  type="button" 
                  class="dest-tab-btn" 
                  :class="{ active: cloneModal.form.destinationType === 'existing' }"
                  @click="cloneModal.form.destinationType = 'existing'"
                >
                  Select Existing Users
                </button>
                <button 
                  type="button" 
                  class="dest-tab-btn" 
                  :class="{ active: cloneModal.form.destinationType === 'new' }"
                  @click="cloneModal.form.destinationType = 'new'"
                >
                  Create New User
                </button>
              </div>
            </div>

            <!-- Existing Users Selection List -->
            <div v-show="cloneModal.form.destinationType === 'existing'" class="form-group" style="margin-top: 10px; position: relative;">
              <div class="custom-dropdown-container">
                <button 
                  type="button" 
                  class="custom-dropdown-trigger" 
                  @click="targetDropdownOpen = !targetDropdownOpen"
                >
                  <span v-if="selectedTargetUser">
                    {{ selectedTargetUser.fullName || selectedTargetUser.username || selectedTargetUser.email }} ({{ selectedTargetUser.email }})
                  </span>
                  <span v-else class="placeholder-text">Select Target User...</span>
                  <i class="fa-solid fa-chevron-down select-arrow" :class="{ rotated: targetDropdownOpen }"></i>
                </button>
                
                <div v-show="targetDropdownOpen" class="custom-dropdown-menu">
                  <div class="dropdown-options-list">
                    <div 
                      v-for="u in filteredTargetUsers" 
                      :key="u.id" 
                      class="dropdown-option-item"
                      :class="{ selected: cloneModal.form.targetUserId === u.id }"
                      @click="selectTargetUser(u)"
                    >
                      {{ u.fullName || u.username || u.email }} ({{ u.email }})
                    </div>
                    <div v-if="filteredTargetUsers.length === 0" class="no-options-found">
                      No users found.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Create New User Form -->
            <div v-show="cloneModal.form.destinationType === 'new'" class="new-user-form-grid" style="margin-top: 10px;">
              <div class="form-group" style="margin-bottom: 12px;">
                <label for="newFullName">New Full Name</label>
                <input
                  id="newFullName"
                  type="text"
                  v-model="cloneModal.form.newUser.fullName"
                  placeholder="Full Name (e.g. John Doe)"
                  :required="cloneModal.form.destinationType === 'new'"
                  class="clone-input"
                />
              </div>
              <div class="form-group" style="margin-bottom: 12px;">
                <label for="newEmail">New Email</label>
                <input
                  id="newEmail"
                  type="email"
                  v-model="cloneModal.form.newUser.email"
                  placeholder="new-user@example.com"
                  :required="cloneModal.form.destinationType === 'new'"
                  class="clone-input"
                />
              </div>
              <div class="form-group">
                <label for="newPassword">New Password</label>
                <input
                  id="newPassword"
                  type="password"
                  v-model="cloneModal.form.newUser.password"
                  placeholder="Password (min 8 characters)"
                  :required="cloneModal.form.destinationType === 'new'"
                  class="clone-input"
                />
              </div>
            </div>

            <!-- Clone Options Section -->
            <div class="clone-options-section" style="margin-top: 20px;">
              <h3 class="section-subtitle">Copy Options</h3>
              <div class="options-grid">
                <label class="checkbox-label">
                  <input type="checkbox" v-model="cloneModal.form.options.favorites" />
                  <span class="checkbox-text">Favorites & Calls</span>
                </label>
                <label class="checkbox-label">
                  <input type="checkbox" v-model="cloneModal.form.options.exclusions" />
                  <span class="checkbox-text">Exclusion Groups</span>
                </label>
                <label class="checkbox-label">
                  <input type="checkbox" v-model="cloneModal.form.options.metrics" />
                  <span class="checkbox-text">Metrics Config</span>
                </label>
              </div>
              <div class="overwrite-option-wrapper" style="margin-top: 12px; padding-top: 10px;">
                <label class="checkbox-label warning-checkbox">
                  <input type="checkbox" v-model="cloneModal.form.overwrite" />
                  <span class="warning-text">Overwrite existing configuration on target accounts</span>
                </label>
              </div>
            </div>

            <div class="modal-actions" style="margin-top: 20px;">
              <button type="button" @click="closeCloneModal" class="btn-cancel">
                Cancel
              </button>
              <button type="submit" class="btn-save">
                {{ cloneModal.isMerge ? 'Merge Settings' : 'Clone Settings' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import UserService from '@/services/UserService';
import { getCurrentUser } from '@/services/authStorage';

export default {
  name: 'AccountManagement',
  data() {
    return {
      users: [],
      prodUsers: [],
      sourceDropdownOpen: false,
      targetDropdownOpen: false,
      sourceSearchQuery: '',
      targetSearchQuery: '',
      currentUser: getCurrentUser() || {},
      isLoading: false,
      showPassword: false,
      filters: {
        search: '',
        role: 'All',
        status: 'ACTIVE'
      },
      currentPage: 1,
      itemsPerPage: 5,
      modal: {
        isOpen: false,
        mode: 'create', // 'create' or 'edit'
        userId: null,
        form: {
          username: '',
          fullName: '',
          email: '',
          password: '',
          role: 'NORMAL_USER',
          status: 'ACTIVE'
        }
      },
      cloneModal: {
        isOpen: false,
        isMerge: false,
        userId: null,
        sourceName: '',
        form: {
          destinationType: 'existing', // 'existing' or 'new'
          targetUserId: null,
          newUser: {
            email: '',
            fullName: '',
            password: ''
          },
          options: {
            favorites: true,
            exclusions: true,
            metrics: true
          },
          overwrite: true
        }
      }
    };
  },
  async created() {
    await this.loadUsers();
  },
  mounted() {
    document.addEventListener('click', this.handleOutsideClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick);
  },
  computed: {
    displayUsers() {
      return this.users.map(this.mapUser);
    },
    filteredUsers() {
      return this.displayUsers.filter(user => {
        const query = this.filters.search.toLowerCase().trim();
        const matchesSearch = !query || 
          String(user.username || '').toLowerCase().includes(query) || 
          String(user.fullName || '').toLowerCase().includes(query) || 
          String(user.email || '').toLowerCase().includes(query);

        const matchesRole = this.filters.role === 'All' || user.role === this.filters.role;

        const matchesStatus = this.filters.status === 'All' || user.status === this.filters.status;

        return matchesSearch && matchesRole && matchesStatus;
      });
    },
    totalPages() {
      return Math.ceil(this.filteredUsers.length / this.itemsPerPage);
    },
    paginatedUsers() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredUsers.slice(start, end);
    },
    showingStart() {
      if (this.filteredUsers.length === 0) return 0;
      return (this.currentPage - 1) * this.itemsPerPage + 1;
    },
    showingEnd() {
      return Math.min(this.currentPage * this.itemsPerPage, this.filteredUsers.length);
    },
    isSystemCurrentUser() {
      return this.currentUser.role === 'SYSTEM';
    },
    filteredSourceUsers() {
      if (this.cloneModal.isMerge) {
        return this.users;
      }
      return this.prodUsers;
    },
    filteredTargetUsers() {
      const sourceList = this.cloneModal.isMerge ? this.users : this.prodUsers;
      const sourceUser = sourceList.find(u => u.id === this.cloneModal.userId);
      const sourceEmail = sourceUser ? sourceUser.email : null;

      return this.users.filter(u => {
        return !sourceEmail || u.email !== sourceEmail;
      });
    },
    selectedSourceUser() {
      const sourceList = this.cloneModal.isMerge ? this.users : this.prodUsers;
      return sourceList.find(u => u.id === this.cloneModal.userId);
    },
    selectedTargetUser() {
      return this.users.find(u => u.id === this.cloneModal.form.targetUserId);
    }
  },
  methods: {
    handleOutsideClick(e) {
      if (!e.target.closest('.custom-dropdown-container')) {
        this.sourceDropdownOpen = false;
        this.targetDropdownOpen = false;
      }
    },
    selectSourceUser(user) {
      this.cloneModal.userId = user.id;
      this.sourceDropdownOpen = false;
      this.sourceSearchQuery = '';

      this.$nextTick(() => {
        const targetUsers = this.filteredTargetUsers;
        if (targetUsers.length > 0) {
          if (!targetUsers.some(u => u.id === this.cloneModal.form.targetUserId)) {
            this.cloneModal.form.targetUserId = targetUsers[0].id;
          }
        } else {
          this.cloneModal.form.targetUserId = null;
        }
      });
    },
    selectTargetUser(user) {
      this.cloneModal.form.targetUserId = user.id;
      this.targetDropdownOpen = false;
      this.targetSearchQuery = '';
    },
    async loadUsers() {
      this.isLoading = true;
      try {
        this.users = await UserService.getUsers();
      } catch (e) {
        this.$toast.open({
          message: e?.response?.data?.message || 'Failed to load users.',
          type: 'error',
          position: 'top-right'
        });
      } finally {
        this.isLoading = false;
      }
    },
    mapUser(user) {
      const username = user.email ? user.email.split('@')[0] : `user-${user.id}`;
      const roleLabel = user.role === 'SYSTEM' ? 'SYSTEM' : user.role === 'ADMIN' ? 'ADMIN' : 'NORMAL USER';
      const statusLabel = user.status || 'ACTIVE';
      return {
        ...user,
        username,
        roleLabel,
        statusLabel,
        roleClass: user.role === 'SYSTEM' ? 'role-super-admin' : user.role === 'ADMIN' ? 'role-admin' : 'role-user',
        statusClass: `status-${String(user.status || 'ACTIVE').toLowerCase()}`,
        avatarColor: this.avatarColor(user.id)
      };
    },
    avatarColor(id) {
      const colors = ['#8b5cf6', '#f59e0b', '#10b981', '#3b82f6', '#ec4899', '#06b6d4', '#14b8a6', '#6366f1'];
      return colors[Number(id || 0) % colors.length];
    },
    isSelf(user) {
      return Number(user.id) === Number(this.currentUser.id);
    },
    isProtectedSuperAdmin(user) {
      return this.currentUser.role === 'ADMIN' && user.role === 'SYSTEM';
    },
    showSuperAdminRestriction() {
      this.$toast.open({
        message: 'Admin accounts cannot modify SYSTEM accounts.',
        type: 'error',
        position: 'top-right'
      });
    },
    getAccountErrorMessage(error, fallback) {
      const message = error?.response?.data?.message || fallback;
      if (this.currentUser.role === 'ADMIN' && error?.response?.status === 403) {
        return 'Admin accounts cannot modify SYSTEM accounts.';
      }
      return String(message).includes('SYSTEM')
        ? 'Admin accounts cannot modify SYSTEM accounts.'
        : message;
    },
    resetFilters() {
      this.filters = {
        search: '',
        role: 'All',
        status: 'ACTIVE'
      };
      this.currentPage = 1;
      this.$toast.open({
        message: 'Filters reset.',
        type: 'info',
        position: 'top-right'
      });
    },
    openCreateModal() {
      this.showPassword = false;
      this.modal.isOpen = true;
      this.modal.mode = 'create';
      this.modal.userId = null;
      this.modal.form = {
        username: '',
        fullName: '',
        email: '',
        password: '',
        role: 'NORMAL_USER',
        status: 'ACTIVE'
      };
    },
    openEditModal(user) {
      if (this.isProtectedSuperAdmin(user)) {
        this.showSuperAdminRestriction();
        return;
      }
      this.modal.isOpen = true;
      this.modal.mode = 'edit';
      this.modal.userId = user.id;
      this.modal.form = {
        fullName: user.fullName,
        email: user.email,
        password: '',
        role: user.role,
        status: user.status
      };
    },
    closeModal() {
      this.modal.isOpen = false;
    },
    async openCloneModal() {
      try {
        this.prodUsers = await UserService.getProdUsers();
        this.cloneModal = {
          isOpen: true,
          isMerge: false,
          userId: this.prodUsers.length > 0 ? this.prodUsers[0].id : null,
          sourceName: '',
          form: {
            destinationType: 'existing',
            targetUserId: null,
            newUser: {
              email: '',
              fullName: '',
              password: ''
            },
            options: {
              favorites: true,
              exclusions: true,
              metrics: true
            },
            overwrite: true
          }
        };
        
        // Mặc định chọn tài khoản đích đầu tiên sau khi load xong danh sách target
        this.$nextTick(() => {
          if (this.filteredTargetUsers.length > 0) {
            this.cloneModal.form.targetUserId = this.filteredTargetUsers[0].id;
          }
        });
      } catch (e) {
        this.$toast.open({
          message: 'Failed to load Production users.',
          type: 'error',
          position: 'top-right'
        });
      }
    },
    async openMergeModal() {
      try {
        this.cloneModal = {
          isOpen: true,
          isMerge: true,
          userId: this.users.length > 0 ? this.users[0].id : null,
          sourceName: '',
          form: {
            destinationType: 'existing',
            targetUserId: null,
            newUser: {
              email: '',
              fullName: '',
              password: ''
            },
            options: {
              favorites: true,
              exclusions: true,
              metrics: true
            },
            overwrite: true
          }
        };
        
        // Mặc định chọn tài khoản đích đầu tiên sau khi load xong danh sách target
        this.$nextTick(() => {
          if (this.filteredTargetUsers.length > 0) {
            this.cloneModal.form.targetUserId = this.filteredTargetUsers[0].id;
          }
        });
      } catch (e) {
        this.$toast.open({
          message: 'Failed to initialize Merge Account.',
          type: 'error',
          position: 'top-right'
        });
      }
    },
    closeCloneModal() {
      this.cloneModal.isOpen = false;
    },
    isValidEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || '').trim());
    },
    showInvalidEmailMessage() {
      this.$toast.open({
        message: 'Please enter a valid email address.',
        type: 'error',
        position: 'top-right'
      });
    },
    async submitCloneModal() {
      const form = this.cloneModal.form;
      if (form.destinationType === 'existing' && !form.targetUserId) {
        this.$toast.open({
          message: 'Please select a target user.',
          type: 'error',
          position: 'top-right'
        });
        return;
      }

      try {
        const payload = {
          targetUserIds: form.destinationType === 'existing' ? [form.targetUserId] : null,
          newUser: form.destinationType === 'new' ? form.newUser : null,
          options: form.options,
          overwrite: form.overwrite
        };

        if (this.cloneModal.isMerge) {
          await UserService.mergeUser(this.cloneModal.userId, payload);
        } else {
          await UserService.cloneUser(this.cloneModal.userId, payload);
        }
        await this.loadUsers();
        this.$toast.open({
          message: this.cloneModal.isMerge ? 'User settings merged successfully.' : 'User settings cloned successfully.',
          type: 'success',
          position: 'top-right'
        });
        this.closeCloneModal();
      } catch (e) {
        this.$toast.open({
          message: this.getAccountErrorMessage(e, this.cloneModal.isMerge ? 'Failed to merge user settings.' : 'Failed to clone user settings.'),
          type: 'error',
          position: 'top-right'
        });
      }
    },
    async submitModal() {
      const email = this.modal.form.email.trim();
      if (!this.isValidEmail(email)) {
        this.showInvalidEmailMessage();
        return;
      }
      if (this.modal.mode === 'create' && this.modal.form.password.length < 8) {
        this.$toast.open({
          message: 'Password must be at least 8 characters.',
          type: 'error',
          position: 'top-right'
        });
        return;
      }

      try {
        const payload = {
          fullName: this.modal.form.fullName,
          email,
          role: this.modal.form.role,
          status: this.modal.form.status
        };
        if (this.modal.mode === 'create') {
          payload.password = this.modal.form.password;
          await UserService.createUser(payload);
        } else {
          await UserService.updateUser(this.modal.userId, payload);
        }
        await this.loadUsers();
        this.$toast.open({
          message: this.modal.mode === 'create' ? 'Account created successfully.' : 'User details updated successfully.',
          type: 'success',
          position: 'top-right'
        });
        this.closeModal();
      } catch (e) {
        this.$toast.open({
          message: this.getAccountErrorMessage(e, 'Failed to save account.'),
          type: 'error',
          position: 'top-right'
        });
      }
    },
    async toggleStatus(user) {
      if (this.isSelf(user)) return;
      if (this.isProtectedSuperAdmin(user)) {
        this.showSuperAdminRestriction();
        return;
      }
      const nextStatus = user.status === 'DISABLED' ? 'ACTIVE' : 'DISABLED';
      try {
        await UserService.updateUser(user.id, { status: nextStatus });
        await this.loadUsers();
        this.$toast.open({
          message: nextStatus === 'ACTIVE' ? `User ${user.username} enabled.` : `User ${user.username} disabled.`,
          type: nextStatus === 'ACTIVE' ? 'success' : 'warning',
          position: 'top-right'
        });
      } catch (e) {
        this.$toast.open({
          message: this.getAccountErrorMessage(e, 'Failed to update user status.'),
          type: 'error',
          position: 'top-right'
        });
      }
    },
    async deleteUser(user) {
      if (this.isSelf(user)) return;
      if (this.isProtectedSuperAdmin(user)) {
        this.showSuperAdminRestriction();
        return;
      }
      if (confirm(`Are you sure you want to delete user ${user.username}?`)) {
        try {
          await UserService.deleteUser(user.id);
          await this.loadUsers();
          this.$toast.open({
            message: `User ${user.username} deleted.`,
            type: 'success',
            position: 'top-right'
          });
          if (this.currentPage > this.totalPages && this.currentPage > 1) {
            this.currentPage--;
          }
        } catch (e) {
          this.$toast.open({
            message: this.getAccountErrorMessage(e, 'Failed to delete user.'),
            type: 'error',
            position: 'top-right'
          });
        }
      }
    },
    async resetUserPassword(user) {
      if (this.isProtectedSuperAdmin(user)) {
        this.showSuperAdminRestriction();
        return;
      }
      if (!confirm(`Reset password for ${user.username} to the default password (Zaq12wsx)?`)) {
        return;
      }
      try {
        await UserService.resetPassword(user.id);
        this.$toast.open({
          message: `Password reset to default for ${user.username}.`,
          type: 'success',
          position: 'top-right'
        });
      } catch (e) {
        this.$toast.open({
          message: this.getAccountErrorMessage(e, 'Failed to reset password.'),
          type: 'error',
          position: 'top-right'
        });
      }
    }
  }
};
</script>

<style scoped>
/* Main Wrapper */
.management-page-wrapper {
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px 16px;
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  text-align: left;
}

/* Breadcrumbs */
.breadcrumb-container {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  margin-bottom: 20px;
}

.breadcrumb-link {
  color: #7c3aed;
  text-decoration: none;
  font-weight: 500;
  transition: opacity 0.2s ease;
}

.breadcrumb-link:hover {
  opacity: 0.8;
}

.breadcrumb-separator {
  color: #94a3b8;
}

.breadcrumb-current {
  color: #64748b;
}

/* Page Title Stats layout */
.page-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 28px;
  flex-wrap: wrap;
  gap: 16px;
}

.page-title-group {
  text-align: left;
}

.page-title {
  font-size: 28px;
  font-weight: 800;
  color: #1e293b;
  margin: 0 0 6px 0;
}

.page-subtitle {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

/* Stats panel */
.stats-container {
  display: flex;
  gap: 16px;
}

.stat-card {
  background: #ffffff;
  border-radius: 12px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.02);
  border: 1px solid rgba(0, 0, 0, 0.04);
  min-width: 180px;
}

.stat-icon-wrapper {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #f1f0fe;
  color: #7c3aed;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}

.stat-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.stat-label {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
}

.stat-value {
  font-size: 20px;
  font-weight: 800;
  color: #1e293b;
  line-height: 1.2;
  margin-top: 2px;
}

/* Filters bar card */
.filters-bar-card {
  background: #ffffff;
  border-radius: 14px;
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.02);
  border: 1px solid rgba(0, 0, 0, 0.04);
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
}

.filters-left-group {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

/* Search input wrapper */
.search-input-wrapper {
  position: relative;
  width: 260px;
}

.search-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 14px;
}

.search-input {
  width: 100%;
  padding: 10px 14px 10px 38px;
  font-size: 14px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  outline: none;
  background: #ffffff;
  color: #0f172a;
  transition: all 0.2s ease;
  box-sizing: border-box;
}

.search-input:focus {
  border-color: #7c3aed;
  box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.12);
}

/* Select input filters */
.select-filter-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-label {
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
}

.select-input-container {
  position: relative;
}

.filter-select {
  padding: 10px 32px 10px 14px;
  font-size: 14px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  outline: none;
  background: #ffffff;
  color: #0f172a;
  appearance: none;
  cursor: pointer;
  min-width: 130px;
  transition: all 0.2s ease;
  box-sizing: border-box;
}

.filter-select:focus {
  border-color: #7c3aed;
}

.select-arrow {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 11px;
  color: #64748b;
  pointer-events: none;
}

/* Buttons */
.btn-reset {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  color: #475569;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-reset:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

.btn-create {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: linear-gradient(135deg, #3981e5 0%, #f748ba 100%);
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(63, 129, 229, 0.2);
  transition: all 0.2s ease;
}

.btn-create:hover {
  opacity: 0.95;
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(63, 129, 229, 0.35);
}

.btn-create:active {
  transform: translateY(0);
}

/* Table Card Panel */
.table-card {
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.02);
  border: 1px solid rgba(0, 0, 0, 0.04);
  padding: 10px 0 0 0;
  overflow: hidden;
}

.table-responsive {
  width: 100%;
  overflow-x: auto;
}

.users-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
}

.users-table th, .users-table td {
  padding: 14px 20px;
  font-size: 14px;
  border-bottom: 1px solid #f1f5f9;
}

.users-table th {
  background: #f8fafc;
  color: #475569;
  font-weight: 700;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

/* Table columns sizing */
.col-num { width: 60px; text-align: center; }
.col-user { min-width: 240px; }
.col-email { min-width: 240px; color: #475569; }
.col-role { width: 140px; }
.col-status { width: 140px; }
.col-actions { width: 220px; text-align: right; }

.text-muted {
  color: #94a3b8;
}

.no-records {
  text-align: center;
  padding: 40px;
  color: #64748b;
  font-size: 15px;
}

/* User Profile Cell */
.user-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.avatar-circle {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
}

.user-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.user-username-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.user-username {
  font-weight: 700;
  color: #1e293b;
}

.admin-badge {
  background: #ede9fe;
  color: #7c3aed;
  font-size: 9px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 6px;
}

.super-admin-badge {
  background: #fef3c7;
  color: #b45309;
  font-size: 9px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 6px;
}

.user-fullname {
  font-size: 12px;
  color: #64748b;
  margin-top: 1px;
}

/* Role Badges */
.role-badge {
  font-size: 10px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 6px;
  letter-spacing: 0.2px;
}

.role-admin {
  background: #ede9fe;
  color: #7c3aed;
}

.role-super-admin {
  background: #fef3c7;
  color: #b45309;
}

.role-user {
  background: #e0f2fe;
  color: #0369a1;
}

.role-manager {
  background: #ecfeff;
  color: #0891b2;
}

/* Status Badges */
.status-bullet-container {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  padding: 3px 8px;
  border-radius: 6px;
  border: 1px solid transparent;
}

.status-bullet-container .bullet {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

.status-label-text {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.2px;
}

.status-active {
  background: #f0fdf4;
  border-color: #dcfce7;
}

.status-active .bullet {
  background: #22c55e;
}

.status-active .status-label-text {
  color: #15803d;
}

.status-locked {
  background: #fef2f2;
  border-color: #fee2e2;
}

.status-locked .bullet {
  background: #ef4444;
}

.status-locked .status-label-text {
  color: #b91c1c;
}

.status-disabled {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.status-disabled .bullet {
  background: #64748b;
}

.status-disabled .status-label-text {
  color: #475569;
}

.status-deleted {
  background: #fef2f2;
  border-color: #fee2e2;
}

.status-deleted .bullet {
  background: #ef4444;
}

.status-deleted .status-label-text {
  color: #b91c1c;
}

/* Action Icon Buttons */
.actions-buttons-group {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.btn-action-icon {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #cbd5e1;
  background: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;
  font-size: 13px;
}

.btn-action-icon:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.btn-action-icon.edit {
  color: #8b5cf6;
  border-color: #ede9fe;
  background: #faf5ff;
}

.btn-action-icon.edit:hover {
  background: #8b5cf6;
  color: white;
  border-color: #8b5cf6;
}

.btn-action-icon.lock {
  color: #f59e0b;
  border-color: #fef3c7;
  background: #fffbeb;
}

.btn-action-icon.lock:hover {
  background: #f59e0b;
  color: white;
  border-color: #f59e0b;
}

.btn-action-icon.password {
  color: #2563eb;
  border-color: #dbeafe;
  background: #eff6ff;
}

.btn-action-icon.password:hover {
  background: #2563eb;
  color: white;
  border-color: #2563eb;
}

.btn-action-icon.clone {
  color: #0891b2;
  border-color: #cffafe;
  background: #ecfeff;
}

.btn-action-icon.clone:hover {
  background: #0891b2;
  color: white;
  border-color: #0891b2;
}

.btn-action-icon.delete {
  color: #ef4444;
  border-color: #fee2e2;
  background: #fff5f5;
}

.btn-action-icon.delete:hover {
  background: #ef4444;
  color: white;
  border-color: #ef4444;
}

/* Table Footer layout */
.table-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  flex-wrap: wrap;
  gap: 16px;
}

.footer-count-text {
  font-size: 13px;
  color: #64748b;
}

.footer-pagination-group {
  display: flex;
  align-items: center;
  gap: 16px;
}

/* Limit dropdown */
.limit-selector-wrapper {
  position: relative;
}

.limit-select {
  padding: 6px 28px 6px 12px;
  font-size: 13px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  outline: none;
  background: #ffffff;
  color: #0f172a;
  appearance: none;
  cursor: pointer;
  box-sizing: border-box;
}

/* Pagination navigation buttons */
.pagination-buttons {
  display: flex;
  align-items: center;
  gap: 4px;
}

.btn-page {
  min-width: 32px;
  height: 32px;
  padding: 0 6px;
  font-size: 13px;
  font-weight: 600;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #ffffff;
  color: #475569;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.btn-page:hover:not(:disabled) {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.btn-page.active {
  background: #7c3aed;
  color: #ffffff;
  border-color: #7c3aed;
}

.btn-page:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Modal Form Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(15, 23, 42, 0.4);
  backdrop-filter: blur(4px);
  z-index: 5000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-card {
  background: #ffffff;
  border-radius: 16px;
  width: 100%;
  max-width: 540px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.05);
  overflow: hidden;
  animation: modal-zoom 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes modal-zoom {
  from { transform: scale(0.95); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid #f1f5f9;
  background: #f8fafc;
}

.modal-title {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.btn-close-modal {
  border: none;
  background: transparent;
  cursor: pointer;
  color: #64748b;
  font-size: 18px;
  padding: 4px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.btn-close-modal:hover {
  background: #ede9fe;
  color: #7c3aed;
}

.modal-form {
  padding: 24px;
}

.modal-form-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.role-status-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  width: 100%;
}

.role-status-row > .form-group {
  width: 100%;
  min-width: 0;
  align-items: stretch;
}

.clone-source-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 12px;
  margin-bottom: 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.clone-source-label {
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}

.clone-source-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #0f172a;
  font-size: 13px;
  font-weight: 700;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
}

.form-group label {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}

.form-group input {
  width: 100%;
  padding: 10px 14px;
  font-size: 14px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  outline: none;
  background: #ffffff;
  color: #0f172a;
  transition: all 0.2s ease;
  box-sizing: border-box;
}

.form-group input:focus {
  border-color: #7c3aed;
  box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.15);
}

.form-group input:disabled {
  background: #f8fafc;
  color: #94a3b8;
  border-color: #e2e8f0;
  cursor: not-allowed;
}

.password-input-wrapper {
  position: relative;
  width: 100%;
}

.password-input-wrapper input {
  padding-right: 42px;
}

.password-toggle {
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  padding: 4px;
  border: 0;
  background: transparent;
  color: #64748b;
  cursor: pointer;
}

.password-toggle:hover {
  color: #7c3aed;
}

.select-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
}

.select-wrapper select {
  display: block;
  flex: 1 1 auto;
  width: 0 !important;
  max-width: none;
  min-width: 0;
  padding: 10px 36px 10px 14px;
  font-size: 14px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  outline: none;
  background: #ffffff;
  color: #0f172a;
  appearance: none;
  cursor: pointer;
  transition: all 0.2s ease;
  box-sizing: border-box;
}

.select-wrapper select:focus {
  border-color: #7c3aed;
  box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.15);
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
}

.btn-cancel {
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 600;
  color: #475569;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-cancel:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

.btn-save {
  padding: 10px 24px;
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  background: linear-gradient(135deg, #3981e5 0%, #f748ba 100%);
  border: none;
  border-radius: 8px;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(63, 129, 229, 0.2);
  transition: all 0.2s ease;
}

.btn-save:hover {
  opacity: 0.95;
  transform: translateY(-1px);
}

/* Theme Global Dark Mode Support */
:global(.dark-mode) .breadcrumb-link {
  color: #a78bfa;
}

:global(.dark-mode) .breadcrumb-separator {
  color: #475569;
}

:global(.dark-mode) .breadcrumb-current {
  color: #94a3b8;
}

:global(.dark-mode) .page-title {
  color: #f1f5f9;
}

:global(.dark-mode) .page-subtitle {
  color: #94a3b8;
}

:global(.dark-mode) .stat-card {
  background: #1e2329;
  border-color: rgba(255, 255, 255, 0.06);
}

:global(.dark-mode) .stat-icon-wrapper {
  background: rgba(124, 58, 237, 0.15);
  color: #c084fc;
}

:global(.dark-mode) .stat-label {
  color: #94a3b8;
}

:global(.dark-mode) .stat-value {
  color: #eaecef;
}

:global(.dark-mode) .filters-bar-card {
  background: #1e2329;
  border-color: rgba(255, 255, 255, 0.06);
}

:global(.dark-mode) .filter-label {
  color: #cbd5e1;
}

:global(.dark-mode) .search-input {
  background: #2b3139;
  border-color: #475569;
  color: #f1f5f9;
}

:global(.dark-mode) .search-input:focus {
  border-color: #a78bfa;
}

:global(.dark-mode) .filter-select {
  background: #2b3139;
  border-color: #475569;
  color: #f1f5f9;
}

:global(.dark-mode) .filter-select:focus {
  border-color: #a78bfa;
}

:global(.dark-mode) .btn-reset {
  background: transparent;
  color: #cbd5e1;
  border-color: #475569;
}

:global(.dark-mode) .btn-reset:hover {
  background: rgba(255, 255, 255, 0.05);
}

:global(.dark-mode) .table-card {
  background: #1e2329;
  border-color: rgba(255, 255, 255, 0.06);
}

:global(.dark-mode) .users-table th {
  background: #181a20;
  color: #94a3b8;
  border-bottom-color: rgba(255, 255, 255, 0.06);
}

:global(.dark-mode) .users-table td {
  border-bottom-color: rgba(255, 255, 255, 0.06);
  color: #eaecef;
}

:global(.dark-mode) .user-username {
  color: #eaecef;
}

:global(.dark-mode) .user-fullname {
  color: #94a3b8;
}

:global(.dark-mode) .col-email {
  color: #cbd5e1;
}

:global(.dark-mode) .admin-badge {
  background: rgba(124, 58, 237, 0.2);
  color: #c084fc;
}

:global(.dark-mode) .super-admin-badge {
  background: rgba(245, 158, 11, 0.18);
  color: #fbbf24;
}

:global(.dark-mode) .role-admin {
  background: rgba(124, 58, 237, 0.2);
  color: #c084fc;
}

:global(.dark-mode) .role-super-admin {
  background: rgba(245, 158, 11, 0.18);
  color: #fbbf24;
}

:global(.dark-mode) .role-user {
  background: rgba(3, 105, 161, 0.2);
  color: #38bdf8;
}

:global(.dark-mode) .role-manager {
  background: rgba(8, 145, 178, 0.2);
  color: #22d3ee;
}

:global(.dark-mode) .status-active {
  background: rgba(34, 197, 94, 0.12);
  border-color: rgba(34, 197, 94, 0.2);
}

:global(.dark-mode) .status-active .status-label-text {
  color: #4ade80;
}

:global(.dark-mode) .status-locked {
  background: rgba(239, 68, 68, 0.12);
  border-color: rgba(239, 68, 68, 0.2);
}

:global(.dark-mode) .status-locked .status-label-text {
  color: #f87171;
}

:global(.dark-mode) .status-disabled {
  background: rgba(100, 116, 139, 0.12);
  border-color: rgba(100, 116, 139, 0.2);
}

:global(.dark-mode) .status-disabled .status-label-text {
  color: #94a3b8;
}

:global(.dark-mode) .btn-action-icon {
  background: #2b3139;
  border-color: #475569;
}

:global(.dark-mode) .btn-action-icon.edit {
  color: #a78bfa;
  border-color: rgba(167, 139, 250, 0.2);
  background: rgba(167, 139, 250, 0.05);
}

:global(.dark-mode) .btn-action-icon.edit:hover {
  background: #7c3aed;
  color: white;
}

:global(.dark-mode) .btn-action-icon.lock {
  color: #fbbf24;
  border-color: rgba(251, 191, 36, 0.2);
  background: rgba(251, 191, 36, 0.05);
}

:global(.dark-mode) .btn-action-icon.lock:hover {
  background: #f59e0b;
  color: white;
}

:global(.dark-mode) .btn-action-icon.password {
  color: #60a5fa;
  border-color: rgba(96, 165, 250, 0.2);
  background: rgba(96, 165, 250, 0.05);
}

:global(.dark-mode) .btn-action-icon.password:hover {
  background: #2563eb;
  color: white;
}

:global(.dark-mode) .btn-action-icon.clone {
  color: #22d3ee;
  border-color: rgba(34, 211, 238, 0.2);
  background: rgba(34, 211, 238, 0.05);
}

:global(.dark-mode) .btn-action-icon.clone:hover {
  background: #0891b2;
  color: white;
}

:global(.dark-mode) .btn-action-icon.delete {
  color: #f87171;
  border-color: rgba(248, 113, 113, 0.2);
  background: rgba(248, 113, 113, 0.05);
}

:global(.dark-mode) .btn-action-icon.delete:hover {
  background: #ef4444;
  color: white;
}

:global(.dark-mode) .table-footer {
  background: #181a20;
  border-top-color: rgba(255, 255, 255, 0.06);
  color: #cbd5e1;
}

:global(.dark-mode) .footer-count-text {
  color: #94a3b8;
}

:global(.dark-mode) .limit-select {
  background: #2b3139;
  border-color: #475569;
  color: #f1f5f9;
}

:global(.dark-mode) .btn-page {
  background: #2b3139;
  border-color: #475569;
  color: #cbd5e1;
}

:global(.dark-mode) .btn-page:hover:not(:disabled) {
  background: #363c4e;
}

:global(.dark-mode) .btn-page.active {
  background: #7c3aed;
  color: white;
  border-color: #7c3aed;
}

/* Modals Dark Mode skin */
:global(.dark-mode) .modal-card {
  background: #1e2329;
  border-color: rgba(255, 255, 255, 0.06);
}

:global(.dark-mode) .modal-header {
  background: #181a20;
  border-bottom-color: rgba(255, 255, 255, 0.06);
}

:global(.dark-mode) .modal-title {
  color: #f1f5f9;
}

:global(.dark-mode) .clone-source-summary {
  background: #181a20;
  border-color: rgba(255, 255, 255, 0.06);
}

:global(.dark-mode) .clone-source-label {
  color: #94a3b8;
}

:global(.dark-mode) .clone-source-name {
  color: #f1f5f9;
}

:global(.dark-mode) .btn-close-modal:hover {
  background: rgba(124, 58, 237, 0.15);
  color: #a78bfa;
}

:global(.dark-mode) .form-group label {
  color: #cbd5e1;
}

:global(.dark-mode) .form-group input {
  background: #2b3139;
  border-color: #475569;
  color: #f1f5f9;
}

:global(.dark-mode) .form-group input:focus {
  border-color: #a78bfa;
}

:global(.dark-mode) .select-wrapper select {
  background: #2b3139;
  border-color: #475569;
  color: #f1f5f9;
}

:global(.dark-mode) .select-wrapper select:focus {
  border-color: #a78bfa;
}

:global(.dark-mode) .btn-cancel {
  background: transparent;
  color: #cbd5e1;
  border-color: #475569;
}

:global(.dark-mode) .btn-cancel:hover {
  background: rgba(255, 255, 255, 0.05);
}

/* Animations for Modals */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.25s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

/* Custom Dropdown Container */
.custom-dropdown-container {
  position: relative;
  width: 100%;
}
.custom-dropdown-trigger {
  width: 100%;
  padding: 10px 14px;
  font-size: 14px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  outline: none;
  background: #ffffff;
  color: #0f172a;
  box-sizing: border-box;
  text-align: left;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
}
.custom-dropdown-trigger:hover {
  border-color: #94a3b8;
  background-color: #f8fafc;
}
.custom-dropdown-trigger:focus {
  border-color: #7c3aed;
  box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.15);
}
.placeholder-text {
  color: #94a3b8;
}
.select-arrow {
  color: #64748b;
  transition: transform 0.25s ease;
}
.select-arrow.rotated {
  transform: rotate(180deg);
}

/* Custom Dropdown Menu */
.custom-dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 6px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.05);
  z-index: 5100;
  padding: 8px;
  box-sizing: border-box;
  animation: dropdown-fade-in 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}
@keyframes dropdown-fade-in {
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
}


/* Options List */
.dropdown-options-list {
  max-height: 180px;
  overflow-y: auto;
}
.dropdown-option-item {
  padding: 10px 12px;
  font-size: 13px;
  color: #334155;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dropdown-option-item:hover {
  background-color: #f1f5f9;
  color: #0f172a;
}
.dropdown-option-item.selected {
  background-color: #ede9fe;
  color: #7c3aed;
  font-weight: 600;
}
.no-options-found {
  padding: 12px;
  text-align: center;
  color: #64748b;
  font-size: 13px;
}

/* Clone Input (for newUser) */
.clone-input {
  width: 100%;
  padding: 10px 14px;
  font-size: 14px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  outline: none;
  background: #ffffff;
  color: #0f172a;
  box-sizing: border-box;
  transition: all 0.2s ease;
}
.clone-input:focus {
  border-color: #7c3aed;
  box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.15);
}

/* Destination tabs styling */
.destination-tabs {
  display: flex;
  gap: 8px;
  margin-top: 6px;
  background-color: #f1f5f9;
  padding: 4px;
  border-radius: 6px;
  width: 100%;
  box-sizing: border-box;
}
.dest-tab-btn {
  flex: 1;
  background: transparent;
  border: none;
  color: #64748b;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.2s ease;
}
.dest-tab-btn.active {
  background-color: #ffffff;
  color: #7c3aed;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}
.dest-tab-btn:hover:not(.active) {
  color: #0f172a;
}

/* Clone options */
.clone-options-section {
  margin-top: 20px;
  background-color: #f8fafc;
  padding: 15px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}
.section-subtitle {
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  margin-bottom: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.options-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.overwrite-option-wrapper {
  border-top: 1px solid #e2e8f0;
}
.warning-checkbox {
  color: #b45309;
}
.warning-text {
  font-size: 12px;
  font-weight: 500;
}
.new-user-form-grid {
  border: 1px solid #cbd5e1;
  padding: 15px;
  border-radius: 8px;
  background-color: #f8fafc;
}

/* ==========================================
   Dark Mode Styles for Clone Settings
   ========================================== */
:global(.dark-mode) .custom-dropdown-trigger {
  background: #2b3139;
  border-color: #475569;
  color: #f1f5f9;
  box-shadow: none;
}
:global(.dark-mode) .custom-dropdown-trigger:hover {
  background-color: #363d4a;
}
:global(.dark-mode) .custom-dropdown-trigger:focus {
  border-color: #7c3aed;
}
:global(.dark-mode) .custom-dropdown-menu {
  background: #1e2329;
  border-color: #2b3139;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.4);
}

:global(.dark-mode) .dropdown-option-item {
  color: #eaecef;
}
:global(.dark-mode) .dropdown-option-item:hover {
  background-color: #2b3139;
  color: #f1f5f9;
}
:global(.dark-mode) .dropdown-option-item.selected {
  background-color: #7c3aed;
  color: #ffffff;
}
:global(.dark-mode) .no-options-found {
  color: #848e9c;
}
:global(.dark-mode) .clone-input {
  background: #2b3139;
  border-color: #475569;
  color: #f1f5f9;
}
:global(.dark-mode) .clone-input:focus {
  border-color: #7c3aed;
}

:global(.dark-mode) .destination-tabs {
  background-color: #1e2329;
}
:global(.dark-mode) .dest-tab-btn {
  color: #848e9c;
}
:global(.dark-mode) .dest-tab-btn.active {
  background-color: #2b3139;
  color: #f0b90b;
  border: 1px solid rgba(240, 185, 11, 0.2);
}
:global(.dark-mode) .dest-tab-btn:hover:not(.active) {
  color: #eaecef;
}

:global(.dark-mode) .clone-options-section {
  background-color: #1e2329;
  border-color: rgba(255, 255, 255, 0.06);
}
:global(.dark-mode) .section-subtitle {
  color: #848e9c;
}
:global(.dark-mode) .overwrite-option-wrapper {
  border-top-color: #2b3139;
}
:global(.dark-mode) .warning-checkbox {
  color: #f0b90b;
}
:global(.dark-mode) .new-user-form-grid {
  border-color: #2b3139;
  background-color: #181a20;
}
</style>
